import React, { useMemo } from 'react';
import { Alert } from '@planne-software/uni/Alert';
import { IconAlertTriangle } from '@tabler/icons-react';

import Loading from '@src/components/ui2/Loading';
import { useTranslate } from '@src/core/hooks';
import { needsPhoneVerificationUnlessPaymentLink, type PaymentOption } from '@src/domains/payments';
import { PhoneVerificationAlert } from '@src/features/customers/auth/phone-verification';

import { PaymentMethodAccordion } from './PaymentMethodAccordion';
import { MethodList, MethodListState } from './styles';

type PaymentMethodListProps = {
  options: PaymentOption[];
  loading: boolean | undefined;
  selected: PaymentOption | undefined;
  onSelect: (option: PaymentOption) => void;
  phoneVerifiedAt?: string | null;
  /** Payment, sale and order links are exempt from phone verification, as they are on the legacy form. */
  directLinkType?: string;
  groupLabel?: string;
  renderBody?: (option: PaymentOption) => React.ReactNode;
};

export const PaymentMethodList = (props: PaymentMethodListProps) => {
  const { options, loading, selected, onSelect, phoneVerifiedAt, directLinkType, renderBody, groupLabel } = props;
  const { i18n } = useTranslate();

  // `options` keeps its identity between renders (usePaymentOptions memoizes it), so this survives them.
  const awaitingVerification = useMemo(
    () =>
      new Set(
        options
          .filter((option) =>
            needsPhoneVerificationUnlessPaymentLink({ paymentOption: option, phoneVerifiedAt, directLinkType }),
          )
          .map((option) => option.paymentMethod.id),
      ),
    [options, phoneVerifiedAt, directLinkType],
  );

  if (options.length === 0) {
    return (
      <MethodListState>
        {loading !== false ? (
          <div data-testid='payment-method-list-loading'>
            <Loading size={24} fullWidth={false} />
          </div>
        ) : (
          <div data-testid='payment-method-list-empty'>
            <Alert
              severity='warning'
              startIcon={<IconAlertTriangle size={20} aria-hidden />}
              title={i18n('checkout_payment_gateway_mismatch_error_title')}
              subTitle={i18n('checkout_payment_gateway_mismatch_error_description')}
            />
          </div>
        )}
      </MethodListState>
    );
  }

  return (
    <MethodList data-testid='payment-method-list' role='radiogroup' aria-label={groupLabel}>
      {options.map((option) => (
        <PaymentMethodAccordion
          key={option.paymentMethod.id}
          option={option}
          selected={option.paymentMethod.id === selected?.paymentMethod.id}
          onSelect={onSelect}
        >
          {awaitingVerification.has(option.paymentMethod.id) ? (
            <div data-testid='payment-method-phone-verification'>
              <PhoneVerificationAlert />
            </div>
          ) : (
            renderBody?.(option) ?? null
          )}
        </PaymentMethodAccordion>
      ))}
    </MethodList>
  );
};

export default PaymentMethodList;
